import { useEffect, useRef, useState } from 'react';

// Debounced draft autosave + "unsaved changes" beforeunload guard.
// `saveFn` is called with the latest `data` after `delay` ms of inactivity.
export default function useAutosaveDraft(data, saveFn, { delay = 3000, enabled = true } = {}) {
    const [isDirty, setIsDirty] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [lastSavedAt, setLastSavedAt] = useState(null);
    const timerRef = useRef(null);
    const firstRun = useRef(true);

    useEffect(() => {
        if (!enabled) return undefined;
        if (firstRun.current) {
            firstRun.current = false;
            return undefined;
        }
        setIsDirty(true);
        clearTimeout(timerRef.current);
        timerRef.current = setTimeout(async () => {
            setIsSaving(true);
            try {
                await saveFn(data);
                setIsDirty(false);
                setLastSavedAt(new Date());
            } catch {
                // Autosave failures are non-fatal; the admin can still save/send manually.
            } finally {
                setIsSaving(false);
            }
        }, delay);
        return () => clearTimeout(timerRef.current);
    }, [JSON.stringify(data), enabled, delay]);

    useEffect(() => {
        const handler = (e) => {
            if (!isDirty) return undefined;
            e.preventDefault();
            e.returnValue = '';
            return '';
        };
        window.addEventListener('beforeunload', handler);
        return () => window.removeEventListener('beforeunload', handler);
    }, [isDirty]);

    return { isDirty, isSaving, lastSavedAt };
}
